/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React, { useState } from 'react';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import {
  ContentServiceV1 as ContentService,
  ContentChangeListener,
} from '@volkswagen-onehub/audi-content-service';
import { I18nextProvider } from 'react-i18next';
import { ThemeProvider } from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { ONEGRAPH_API_GATEWAY_URL, IS_T3_WEBSITE, IS_MOCK } from './constants/environment';
import i18n from '../i18n';
import GlobalStyles from './styles';
import { COLORS } from './styles/variables';
import ErrorBoundary from './components/ErrorBoundary';
import VehicleProvider from './components/VehicleProvider';
import CTAProvider from './components/CTAProvider';
import ContentWrapper from './components/ContentWrapper';
import { AppContent, mapContent } from './components/ContentMapping/contentMapping';
import { getEnabledVendorCTAs } from './utils/getEnabledVendorCTAs';
import PriceConfigProvider from './components/PriceConfigProvider';
import { getMockContent } from './mockContents/MockContents';

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: ONEGRAPH_API_GATEWAY_URL,
});

const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, httpLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

interface FeatureAppProps {
  contentService?: ContentService;
}

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const { contentService } = inherited;

  const [rawContent, setRawContent] = useState(contentService?.getContent() as AppContent);

  contentService?.onContentChange(setRawContent as ContentChangeListener);

  const content = mapContent(rawContent ?? getMockContent(4));
  console.log('🚀 ~ content:', content);

  const enabledCTAsOnly = getEnabledVendorCTAs(content as any);
  console.log('🚀 ~ enabledCTAsOnly:', enabledCTAsOnly);

  const PriceConfigModel = content?.priceConfigModel;

  // React.useEffect(() => {
  //   // eslint-disable-next-line no-param-reassign
  //   trackingService.featureAppName = __FEATURE_APP_NAME__;
  //   const readyEvent = {
  //     event: {
  //       attributes: {
  //         componentName: __FEATURE_APP_NAME__,
  //         elementName: '',
  //         label: '',
  //         clickID: '',
  //         pos: '',
  //         targetURL: '',
  //         value: '',
  //       },
  //       eventInfo: {
  //         eventAction: 'feature_app_ready',
  //         eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
  //       },
  //     },
  //   };
  //   trackingService.track(readyEvent);
  // }, []);

  return (
    <I18nextProvider i18n={i18n}>
      {/* @ts-ignore:next-line */}
      <ErrorBoundary>
        <ThemeProvider theme={COLORS}>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider isTier3={IS_T3_WEBSITE} isMock={IS_MOCK}>
              <PriceConfigProvider priceConfig={PriceConfigModel}>
                <CTAProvider tier3Ctas={enabledCTAsOnly}>
                  <div className="resetWrapper">
                    <ContentWrapper />
                  </div>
                </CTAProvider>
              </PriceConfigProvider>
            </VehicleProvider>
          </ApolloProvider>
          <GlobalStyles />
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
